// @flow

/**
 * Download Actions Component
 * Renders download icon buttons for agent messages.
 * Resolves data-display tags before export (tables → markdown, charts → images).
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { useDownloadActions } from '../context';
import { buildDownloadHeading, prependHeadingToMarkdown, formatDownloadTimestampForFilename } from '../utils/downloadHeadings';
import { resolveDataDisplaysForExport } from '../utils/resolveDataDisplaysForExport';

import type { DownloadAction } from '../types';

const ActionsContainer = styled.div`
  display: flex;
  gap: 0.25rem;

  /* Match thumbs up/down button hover styling */
  .bp3-button.bp3-minimal {
    &:hover:not(:disabled) {
      color: #1a73e8;
      background-color: rgba(26, 115, 232, 0.04);
    }
  }
`;

type DownloadActionsProps = {
  /** The raw markdown text of the agent message */
  markdownText: string,
  /** Whether the message is currently streaming */
  isStreaming: boolean,
  /** Data display definitions for this message (charts/tables) */
  dataDisplays?: { [string]: Object },
  /**
   * Called with true just before capture and false when export finishes.
   * Lets the message mount its inactive (collapsed) pane so charts in BOTH
   * the summary and detail blocks can be captured as images.
   */
  onExportModeChange?: (enabled: boolean) => void,
};

/**
 * Wait for freshly mounted (clipped-pane) charts to settle before capture:
 * two nested animation frames give ResponsiveContainer time to measure width,
 * then a short delay lets Recharts paint. If captures come back blank,
 * increase this delay before falling back to visibly expanding both panes.
 */
const waitForChartsToSettle = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 250);
      });
    });
  });

/**
 * Generates a filename with current date
 * @param {string} extension - File extension (e.g., 'pdf', 'docx')
 * @returns {string} Filename like 'chat-message-2026-01-06.pdf'
 */
const generateFilename = (extension: string): string => {
  const ts = formatDownloadTimestampForFilename(new Date());
  return `Aura Vision Message ${ts}.${extension}`;
};

const DownloadActions = ({ markdownText, isStreaming, dataDisplays, onExportModeChange }: DownloadActionsProps) => {
  const downloadActions = useDownloadActions();
  const [exporting, setExporting] = useState(false);

  // Don't render if no download actions configured
  if (!downloadActions || downloadActions.length === 0) {
    return null;
  }

  // Determine if buttons should be disabled
  const hasText = Boolean(markdownText && markdownText.trim());
  const isDisabled = isStreaming || !hasText || exporting;

  const handleClick = async (action: DownloadAction) => {
    if (isDisabled) return;

    setExporting(true);
    try {
      // Mount the inactive (collapsed) pane so its charts are in the DOM to
      // capture, then wait for them to lay out and paint before resolving.
      if (onExportModeChange) {
        onExportModeChange(true);
        await waitForChartsToSettle();
      }

      // Resolve data displays (captures chart images, converts tables to markdown)
      const { markdown: resolvedMarkdown, chartImages } =
        await resolveDataDisplaysForExport(markdownText, dataDisplays);

      const extension = action.id;
      const filename = generateFilename(extension);
      const heading = buildDownloadHeading({ kind: 'message' });
      const markdownWithHeading = prependHeadingToMarkdown(resolvedMarkdown, heading);

      await action.handler(markdownWithHeading, filename, chartImages);
    } catch (error) {
      console.error(`Download failed for ${action.id}:`, error);
    } finally {
      // Load-bearing: always tear down the clipped pane, even on failure, so a
      // failed export never leaves a phantom mounted pane behind.
      if (onExportModeChange) onExportModeChange(false);
      setExporting(false);
    }
  };

  return (
    <ActionsContainer>
      {downloadActions.map((action) => (
        <Tooltip2 key={action.id} content={exporting ? 'Preparing download...' : action.tooltip} position="top">
          <Button
            icon={action.icon}
            minimal
            small
            disabled={isDisabled}
            loading={exporting}
            onClick={() => handleClick(action)}
            aria-label={action.tooltip}
          />
        </Tooltip2>
      ))}
    </ActionsContainer>
  );
};

export default DownloadActions;
